import axios from "axios"
import store from "src/store/store"
import AxiosConfig from "../AxiosConfig"
import mutator from "../mutator"
import NotificationTypes from "./Types/NotificationTypes"
import WebWorker from "src/utilities/Notifications/NotificationWebWorker/NotificationWebWorkerSetup"
import notificationsWebWorker from "src/utilities/Notifications/NotificationWebWorker/NotificationsStateWebWorker"
//@ts-ignore
import socket from "../socket"
import dayjs from "dayjs"

const date = new Date()
const timeZoneOffset = -date.getTimezoneOffset()

/* Creating an object with keys and values. */
const triggers: any = {
    new: ["new", "urgent", "warning", "new-form-task", "new-batch-update"],
    urgent: ["urgent"],
    warning: ["warning"],
    todo: ["todo"],
    read: ["read"],
    tasks: ["new-form-task", "new-batch-update"],
    completedTasks: ["completed-form-task", "completed-batch-update"],
    nonMarkable: ["new-form-task", "completed-form-task", "new-batch-update", "completed-batch-update"],
    all: () => {
        const keys: any = Object.entries(triggers)
        let arr = []
        for (let i = 0; i < keys.length; i++) {
            for (let j = 0; j < keys[i][1].length; j++) {
                arr.push(keys[i][1][j])
            }
        }
        return arr
    }
}

/*Creating and sorting the viewTypes array by the order property. */
const viewTypes = [
    { active: false, triggers: triggers.all(), canBatch: true, title: "All", order: 6 },
    { active: true, triggers: triggers.new, canBatch: true, title: "New", order: 1 },
    { active: false, triggers: triggers.todo, canBatch: true, title: "Todo", order: 2 },
    { active: false, triggers: triggers.tasks, canBatch: false, title: "New Tasks", order: 3 },
    { active: false, triggers: triggers.completedTasks, canBatch: false, title: "Completed Tasks", order: 4 },
    // { active: false, triggers: triggers.warning, canBatch: true, title: "Warning", order: 5 },
    // { active: false, triggers: triggers.urgent, canBatch: true, title: "Urgent", order: 6 },
    { active: false, triggers: triggers.read, canBatch: true, title: "Read", order: 5 },
].sort(({ order: a }, { order: b }) => a - b)

/* A store for a React app. */
const notificationStore = {
    stateWebWorker: new WebWorker(notificationsWebWorker),
    timeFormat: "YYYY/MM/DD h:mm a",
    timeZoneOffset: timeZoneOffset,
    operations: [
        { title: "Mark Read", operation: "read", variant: "secondary", bg: "#6c757d", order: 1 },
        { title: "Mark Todo", operation: "todo", variant: "info", bg: "#17a2b8", order: 1 },
        { title: "Mark New", operation: "new", variant: "primary", bg: "#007bff", order: 2 },
        { title: "Delete", operation: "deleted", variant: "danger", bg: "#dc3545", order: 3 },
    ].sort(({ order: a }, { order: b }) => a - b),
    tableItems: [
        { title: "Title", key: "title", order: 2 },
        { title: "Date", key: "timestamp", order: 3, isDate: true },
        { title: "Due", key: "dueDateUtc", order: 4, isDate: true },
        { title: "Text", key: "text", order: 5 },
        { title: "Status", key: "notificationState", order: 1 }
    ].sort(({ order: a }, { order: b }) => a - b),
    nonMenuDismissables: ["urgent", "warning"],
    typingTimeout: 1000,
    viewTypes: viewTypes,
    intervalId: null,
    state: {
        notificationMenuOpen: false,
        notifications: [],
        notificationsCopy: [],
        newCount: 0,
        newNotifications: [],
        newTasks: [],
        openNotification: null,
        openManagementNotification: null,
        allChecked: false,
        checkedList: [],
        busy: false,
        searchTerm: "",
        rowMenuId: -1,
        view: viewTypes[0],
        naggy: { shouldNag: false, tasks: [], error: false },
        ignore: [],
    },
    manageState: (keys: Array<{ key: string, value?: any }>) => {
        //@ts-ignore
        notificationStore.stateWebWorker.postMessage({ action: "updateState", payload: mutator.mutate(notificationStore.state, keys) })
    },
    getNotifications: (userId: string | number) => {
        const expiresStr = store.getState().user[".expires"]
        if (expiresStr && new Date(expiresStr) <= new Date()) {
            notificationStore.killNotifications()
            return
        }
        if (notificationStore.state.searchTerm === "") {
            notificationStore.manageState([{ key: "busy", value: true }])
            axios.get(`${process.env.REACT_APP_API_URL}/api/User/Notifications?userId=${userId}&category=general`, AxiosConfig.configValue("min"))
                .then((res) => {
                    let notifications: Array<NotificationTypes> = res.data
                    const oldList: NotificationTypes[] = notificationStore.state.notifications
                    let checkedList: NotificationTypes[] = []
                    for (let i = 0; i < oldList.length; i++) {//?? yikes... come up with something better
                        for (let j = 0; j < notifications.length; j++) {
                            if (oldList[i].id === notifications[j].id) {
                                /* Drops a check a row is no longer eligible for, so a session
                                that checked one before this rule existed heals on its next poll. */
                                notifications[j].checked = oldList[i].checked && notificationStore.canCheck(notifications[j])
                                /* The freshly fetched row, never the stale one it carried the
                                check over from. checkedList feeds the batch operations, and the
                                API overwrites every column from what they POST, so a row from
                                the previous poll undoes anything the server changed since. */
                                if (notifications[j].checked) {
                                    checkedList.push(notifications[j])
                                }
                            }
                        }
                    }
                    notifications.sort((a, b) => b.id - a.id)
                    const newList = notificationStore.newList(notifications, triggers.new)
                    notificationStore.manageState(
                        [
                            { key: "notifications", value: notifications },
                            { key: "notificationsCopy", value: notifications },
                            { key: "newNotifications", value: newList.newNotifications },
                            { key: "newTasks", value: newList.newTasks },
                            { key: "newCount", value: newList.count },
                            { key: "naggy", value: notificationStore.handleNag(newList.newTasks) },
                            { key: "busy", value: false },
                            { key: "checkedList", value: checkedList }
                        ])
                    // //console.log("notif_debug notificationStore", notificationStore.state.notifications)
                }).catch(() => {
                    notificationStore.manageState([{ key: "busy", value: false }])
                    // snackBar({ text: "Failed to fetch notifications.", type: "error", timeout: 2000 })
                })
        } else {
            setTimeout(() => {
                notificationStore.getNotifications(userId)
                if (notificationStore.state.busy) {
                    notificationStore.manageState([{ key: "busy", value: false }])
                }
            }, 1000)
        }
    },
    /* Taking an array of notifications and an array of strings and returning an object with three
    properties. */
    newList: (notifications: Array<NotificationTypes>, newTrigger: Array<string>) => {
        let count = 0
        let newNotifications = []
        let newTasks = []
        for (let i = 0; i < notifications.length; i++) {
            if (newTrigger.includes(notifications[i].notificationState)) {
                count++
                newNotifications.push(notifications[i])
            }
            if (triggers.tasks.includes(notifications[i].notificationState)) {
                newTasks.push(notifications[i])
            }
        }
        return { newNotifications: newNotifications, newTasks: newTasks, count: count }
    },
    countNotifications: (notifications: Array<NotificationTypes>, triggers: Array<string>) => {
        let count = 0
        for (let i = 0; i < notifications.length; i++) {
            if (triggers.includes(notifications[i].notificationState)) {
                count++
            }
        }
        return count
    },
    /* Filtering the notifications based on the view. */
    handleViewTypeFilter: () => {
        const notifications = notificationStore.state.notifications
        const view = notificationStore.state.view.triggers
        let filteredList: Array<NotificationTypes> = []
        for (let i = 0; i < notifications.length; i++) {
            //@ts-ignore
            if (view.includes(notifications[i].notificationState)) {
                filteredList.push(notifications[i])
            }
        }
        return filteredList
    },
    /* Setting the view to the index that is passed in. */
    setView: (index: number) => {
        let views = viewTypes
        for (let i = 0; i < views.length; i++) {
            views[i].active = false
        }
        views[index].active = true
        notificationStore.checkOrUncheckAll(false)
        notificationStore.manageState([{ key: "view", value: views[index] }])
    },
    /* A function that takes an array of NotificationTypes and returns an object with a property of
    shouldNag, tasks, and error. */
    handleNag: (notificationTasks: Array<NotificationTypes>) => {
        if (!notificationStore.state.naggy.error) {
            const now = dayjs().utcOffset(timeZoneOffset)
            let temp: Array<NotificationTypes> = []
            for (let i = 0; i < notificationTasks.length; i++) {
                const task = notificationTasks[i]
                //@ts-ignore
                const due = dayjs(task.dueDateUtc).utcOffset(timeZoneOffset)
                const timeLeft = due.diff(now, "m")
                //@ts-ignore
                if ((!isNaN(timeLeft) && timeLeft <= 4320) && (!task.completedDateUtc) && notificationStore.state.ignore.indexOf(task.workFlowId) === -1) {
                    temp.push({
                        //@ts-ignore
                        notification: notificationTasks[i],
                        dismisable: timeLeft > 1 ? true : false,
                        timeLeft: timeLeft,
                        due: task.dueDateUtc
                    })
                }
            }
            const naggy = { shouldNag: temp.length > 0 ? true : false, tasks: temp, error: false }
            return naggy
        }
        return { shouldNag: false, tasks: [], error: true }
    },
    ignoreNag: (workFlowId: number, userId: string | number) => {
        let temp: any = notificationStore.state.ignore
        if (temp.indexOf(workFlowId) === -1) {
            temp.push(workFlowId)
            notificationStore.manageState([{ key: "ignore", value: temp }])
            notificationStore.getNotifications(userId)
        }
    },
    unignore: (userId: string | number) => {
        notificationStore.manageState([{ key: "ignore", value: [] }])
        notificationStore.getNotifications(userId)
    },
    addOrUpdateNotification: (notifications: Array<NotificationTypes>, action?: string, uncheck?: boolean) => {
        notificationStore.manageState([{ key: "busy", value: true }, { key: "rowMenuId", value: -1 }])
        let rb = notifications
        for (let i = 0; i < rb.length; i++) {
            if ((!rb[i].dateReadUtc && action) && action === "read") {
                rb[i].dateReadUtc = dayjs.utc().format(notificationStore.timeFormat)
            }
            let nonMarkableIndex = triggers.nonMarkable.indexOf(rb[i].notificationState)
            if (nonMarkableIndex === -1) {
                rb[i].notificationState = action ? action : rb[i].notificationState
            }
            delete rb[i].checked
        }
        if (uncheck) {
            notificationStore.checkOrUncheckAll(false)
        }
        //@ts-ignore
        notificationStore.manageState([{ key: "newCount", value: notificationStore.countNotifications(notificationStore.state.notifications, triggers.new) }])
        axios.post(`${process.env.REACT_APP_API_URL}/api/user/multiplenotifications`, rb, AxiosConfig.configValue("min"))
            .then(() => {
                notificationStore.getNotifications(rb[0].recipientId)
            })
            .catch(() => {
                // snackBar({ text: "Failed to update notifications.", type: "error", timeout: 2000 })
                notificationStore.manageState([{ key: "busy", value: false }])
            })
    },
    setOpenNotification: (notification: NotificationTypes | null, fromManagement?: boolean) => {
        if (fromManagement) {
            notificationStore.manageState([{ key: "openManagementNotification", value: notification }, { key: "allChecked", value: false }])
        } else {
            notificationStore.manageState([{ key: "openNotification", value: notification }, { key: "allChecked", value: false }])
        }
    },
    setChecked: (notification: NotificationTypes) => {
        let notifications = notificationStore.state.notifications
        //@ts-ignore
        let checkedList = []
        for (let i = 0; i < notifications.length; i++) {
            //@ts-ignore
            if (notifications[i].id === notification.id) {
                //@ts-ignore
                notifications[i].checked = !notifications[i].checked
                //@ts-ignore
            }
            //@ts-ignore
            if (notifications[i].checked) {
                checkedList.push(notifications[i])
            }
        }
        //@ts-ignore
        notificationStore.manageState([{ key: "notifications", value: notifications }, { key: "checkedList", value: checkedList }])
    },
    /* A task is an action item the server owns and completes. The client has no state to
    write on one, so it must never reach the batch operations. */
    isTask: (notification: NotificationTypes) => {
        return triggers.nonMarkable.indexOf(notification.notificationState) > -1
    },
    canCheck: (notification: NotificationTypes) => {
        /* Tasks carry workFlowId 0 like any notice, so the id test alone let them be checked.
        A batch operation POSTs the whole cached row and the API overwrites every column from
        it, which reverted a completed task to its pre-approval state. */
        const canCheck = typeof notification.workFlowId === "number" && notification.workFlowId === 0
            && !notificationStore.isTask(notification)
        return canCheck
    },
    checkOrUncheckAll: (checked: boolean) => {//!!! this is terrible, fix this!!!
        let checkedList: NotificationTypes[] = []
        let notifications: NotificationTypes[] = notificationStore.state.notifications
        const notificationsInView: NotificationTypes[] = notificationStore.handleViewTypeFilter()
        if (checked) {
            for (let i = 0; i < notifications.length; i++) {
                for (let j = 0; j < notificationsInView.length; j++) {
                    if ((notifications[i].id === notificationsInView[j].id) && (notificationStore.canCheck(notifications[i]))) {
                        notifications[i].checked = checked
                        checkedList.push(notifications[i])
                    }
                }
            }
        } else {
            for (let i = 0; i < notifications.length; i++) {
                notifications[i].checked = false
            }
            checkedList = []
        }

        notificationStore.manageState(
            [
                { key: "notifications", value: notifications },
                { key: "allChecked", value: checked },
                { key: "checkedCount", value: checked ? checkedList.length : 0 },
                { key: "checkedList", value: checkedList }
            ])
    },
    filterNotifications: (input: string, notifications: Array<NotificationTypes>) => {
        let temp = []
        for (let i = 0; i < notifications.length; i++) {
            let str = Object.values(notifications[i]).join().toLowerCase()
            if (str.includes(input)) {
                temp.push(notifications[i])
            }
        }
        notificationStore.manageState([{ key: "notifications", value: temp }, { key: "busy", value: false }])
    },
    debounce: (event: { target: { value: string } }) => {
        const input = event.target.value.toLowerCase()
        notificationStore.manageState([{ key: "searchTerm", value: input }, { key: "busy", value: true }])
        if (notificationStore.typingTimeout) {
            clearTimeout(notificationStore.typingTimeout);
        }
        //!!r-18-bk
        //@ts-ignore
        notificationStore.typingTimeout = setTimeout(() => {
            notificationStore.checkOrUncheckAll(false)
            if (input.length > 0) {
                notificationStore.filterNotifications(input, notificationStore.state.notificationsCopy)
            } else {
                notificationStore.manageState([{ key: "notifications", value: notificationStore.state.notificationsCopy }, { key: "busy", value: false }])
            }
        }, 1000)
    },
    setUpSockets: (userId: string | number) => {
        if (socket) {
            socket.on("push-notifications-all-users", () => {
                notificationStore.getNotifications(userId)
            })
            socket.on(`${userId}-notifications`, () => {
                notificationStore.getNotifications(userId)
            })
        }
    },
    killNotifications: () => {
        if (notificationStore.intervalId) {
            //@ts-ignore
            clearInterval(notificationStore.intervalId)
        }
    },
    init: (interval: number, userId: string | number) => {
        notificationStore.killNotifications()
        notificationStore.getNotifications(userId)
        //@ts-ignore
        notificationStore.intervalId = setInterval(() => {
            notificationStore.getNotifications(userId)
        }, interval)

        notificationStore.setUpSockets(userId)
    }
}
export default notificationStore